import type { PrivacyLabel, PrivacySpan, RedactionMode } from "./types";

export const LABEL_META: Record<
  PrivacyLabel,
  { title: string; titleEn: string; token: string; className: string }
> = {
  private_person: {
    title: "Person",
    titleEn: "Person",
    token: "[PERSON]",
    className: "person",
  },
  private_email: {
    title: "E-Mail",
    titleEn: "Email",
    token: "[EMAIL]",
    className: "email",
  },
  private_phone: {
    title: "Telefon",
    titleEn: "Phone",
    token: "[TELEFON]",
    className: "phone",
  },
  private_address: {
    title: "Adresse",
    titleEn: "Address",
    token: "[ADRESSE]",
    className: "address",
  },
  private_date: {
    title: "Datum",
    titleEn: "Date",
    token: "[DATUM]",
    className: "date",
  },
  private_url: {
    title: "URL",
    titleEn: "URL",
    token: "[URL]",
    className: "url",
  },
  account_number: {
    title: "Kontonummer",
    titleEn: "Account number",
    token: "[KONTO]",
    className: "account",
  },
  secret: {
    title: "Secret",
    titleEn: "Secret",
    token: "[SECRET]",
    className: "secret",
  },
};

export function labelTitle(label: PrivacyLabel): string {
  return LABEL_META[label]?.title ?? label;
}

export function labelTitleEn(label: PrivacyLabel): string {
  return LABEL_META[label]?.titleEn ?? label;
}

export function replacementFor(label: PrivacyLabel): string {
  return LABEL_META[label]?.token ?? "[PRIVAT]";
}

export function classFor(label: PrivacyLabel): string {
  return LABEL_META[label]?.className ?? "unknown";
}

export function applyRedactions(
  text: string,
  spans: PrivacySpan[],
  mode: RedactionMode = "mask"
): string {
  const active = sortedActiveSpans(spans);
  let result = text;

  for (let index = active.length - 1; index >= 0; index -= 1) {
    const span = active[index];
    result = `${result.slice(0, span.start)}${replacementForSpan(span, mode)}${result.slice(span.end)}`;
  }

  return result;
}

export function replacementForSpan(span: PrivacySpan, mode: RedactionMode): string {
  if (mode === "mask") {
    return replacementFor(span.label);
  }

  return dummyFor(span.label, span.text);
}

export function sortedActiveSpans(spans: PrivacySpan[]): PrivacySpan[] {
  return mergeSpans(
    spans.filter((span) => span.enabled && span.start >= 0 && span.end > span.start)
  ).sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));
}

export function mergeSpans(spans: PrivacySpan[]): PrivacySpan[] {
  const valid = spans
    .filter((span) => span.end > span.start)
    .map((span) => ({
      ...span,
      text: span.text.trim() ? span.text : span.text,
    }))
    .sort((a, b) => a.start - b.start || compareSpanQuality(b, a));

  const merged: PrivacySpan[] = [];

  for (const candidate of valid) {
    const overlapIndexes = merged
      .map((span, index) => (overlaps(span, candidate) ? index : -1))
      .filter((index) => index >= 0);

    if (!overlapIndexes.length) {
      merged.push(candidate);
      continue;
    }

    const overlapping = overlapIndexes.map((index) => merged[index]);
    const canReplaceAll = overlapping.every((span) => compareSpanQuality(candidate, span) > 0);

    if (canReplaceAll) {
      for (let index = overlapIndexes.length - 1; index >= 0; index -= 1) {
        merged.splice(overlapIndexes[index], 1);
      }
      merged.push(candidate);
    }
  }

  return merged
    .sort((a, b) => a.start - b.start || compareSpanQuality(b, a))
    .map((span, index) => ({
      ...span,
      id: `${span.label}-${span.start}-${span.end}-${index}`,
    }));
}

export function confidenceLabel(score: number): string {
  if (!Number.isFinite(score)) return "Muster";
  return `${Math.round(score * 100)}%`;
}

function overlaps(a: PrivacySpan, b: PrivacySpan): boolean {
  return a.start < b.end && b.start < a.end;
}

function compareSpanQuality(a: PrivacySpan, b: PrivacySpan): number {
  const source = sourcePriority(a) - sourcePriority(b);
  if (source !== 0) return source;

  const label = labelPriority(a.label) - labelPriority(b.label);
  if (label !== 0) return label;

  const score = normalizedScore(a.score) - normalizedScore(b.score);
  if (Math.abs(score) >= 0.05) return score;

  return a.end - a.start - (b.end - b.start);
}

function sourcePriority(span: PrivacySpan): number {
  return span.source === "pattern" ? 2 : 1;
}

function labelPriority(label: PrivacyLabel): number {
  switch (label) {
    case "secret":
      return 8;
    case "private_email":
      return 7;
    case "private_url":
      return 6;
    case "account_number":
      return 5;
    case "private_phone":
      return 4;
    case "private_address":
      return 3;
    case "private_date":
      return 2;
    case "private_person":
      return 1;
  }
}

function normalizedScore(score: number): number {
  return Number.isFinite(score) ? score : 0;
}

function dummyFor(label: PrivacyLabel, sourceText: string): string {
  const seed = hash(`${label}:${sourceText.toLowerCase().trim()}`);
  const pick = <T>(values: T[]): T => values[seed % values.length];

  switch (label) {
    case "private_person":
      return pick(["Anna Berger", "Lukas Steiner", "Mira Hoffmann", "Jonas Weber", "Laura Keller"]);
    case "private_email":
      return `${pick(["anna.berger", "lukas.steiner", "mira.hoffmann", "jonas.weber", "laura.keller"])}@beispiel.test`;
    case "private_phone":
      return `+49 30 5550 ${String(1000 + (seed % 9000)).padStart(4, "0")}`;
    case "private_address":
      return pick([
        "Musterstraße 12, 10115 Berlin",
        "Beispielweg 8, 8001 Zürich",
        "Testgasse 4, 1020 Wien",
        "Demoallee 21, 20095 Hamburg",
      ]);
    case "private_date":
      return pick(["15.01.2024", "03.06.2024", "22.09.2024", "11.12.2024"]);
    case "private_url":
      return `https://beispiel.test/projekt-${100 + (seed % 900)}`;
    case "account_number":
      return pick([
        "DE89370400440532013000",
        "CH9300762011623852957",
        "AT611904300234573201",
      ]);
    case "secret":
      return `beispiel-secret-${String(seed).padStart(10, "0")}`;
  }
}

function hash(value: string): number {
  let result = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    result ^= value.charCodeAt(index);
    result = Math.imul(result, 16777619);
  }

  return result >>> 0;
}
